import React, {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useState,
} from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command'
import { Check, ChevronDown, Cross, Plus, LoaderCircle } from 'lucide-react'
import { Button, type ButtonProps } from './button'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'

export type BaseOption = {
  value: string | number
  label: string
}

/**
 * 创建选项的状态类型
 * @typedef {('idle'|'creating'|'finish'|'error')} CreateStatus
 * - idle: 空闲状态，所有功能正常可用
 * - creating: 正在创建选项，显示loading图标，禁用所有交互
 * - finish: 创建成功，显示绿色成功图标和文本，自动重置
 * - error: 创建失败，显示红色错误图标和文本，自动重置
 */
export type CreateStatus = 'idle' | 'creating' | 'finish' | 'error'

/**
 * 创建状态常量
 */
export const CREATE_STATUS = {
  IDLE: 'idle' as const,
  CREATING: 'creating' as const,
  FINISH: 'finish' as const,
  ERROR: 'error' as const,
} satisfies Record<string, CreateStatus>

/**
 * 状态检查辅助函数
 */
export const createStatusUtils = {
  isIdle: (status: CreateStatus): boolean => status === CREATE_STATUS.IDLE,
  isCreating: (status: CreateStatus): boolean =>
    status === CREATE_STATUS.CREATING,
  isFinished: (status: CreateStatus): boolean =>
    status === CREATE_STATUS.FINISH,
  isError: (status: CreateStatus): boolean => status === CREATE_STATUS.ERROR,
  isInteractable: (status: CreateStatus): boolean =>
    status === CREATE_STATUS.IDLE,
  hasResult: (status: CreateStatus): boolean =>
    status === CREATE_STATUS.FINISH || status === CREATE_STATUS.ERROR,
} as const

type SelectValue<
  OPT extends BaseOption,
  IsMultiple extends boolean,
> = IsMultiple extends true ? OPT['value'][] : OPT['value']

type SelectOPT<
  OPT extends BaseOption,
  IsMultiple extends boolean,
> = IsMultiple extends true ? OPT[] : OPT

export interface SelectProps<
  OPT extends BaseOption,
  IsMultiple extends boolean,
> {
  placeholder?: string
  buttonProps?: ButtonProps
  showSearch?: boolean
  searchProps?: ComponentProps<typeof CommandInput>
  contentProps?: ComponentProps<typeof PopoverContent>
  empty?: ReactNode
  options: OPT[]
  value?: SelectValue<OPT, IsMultiple>
  width?: number | string
  onChange?: (val?: SelectValue<OPT, IsMultiple>, opt?: OPT) => void
  allowClear?: boolean
  multiple?: IsMultiple
  disabled?: boolean
  modal?: boolean
  allowCreate?: boolean
  onCreateOption?: (inputValue: string) => OPT | Promise<OPT>
  createText?: string
}

const defaultPlaceholder = 'select item'

export const SelectV2 = <
  OPT extends BaseOption,
  IsMultiple extends boolean = false,
>({
  placeholder = defaultPlaceholder,
  buttonProps,
  showSearch,
  searchProps,
  contentProps,
  empty,
  options,
  value,
  onChange,
  width = 180,
  allowClear,
  multiple,
  disabled,
  modal,
  allowCreate,
  onCreateOption,
  createText,
}: SelectProps<OPT, IsMultiple>) => {
  const t = useTranslations('ui')

  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [createStatus, setCreateStatus] = useState<CreateStatus>(
    CREATE_STATUS.IDLE,
  )
  const [innerValue, setInnerValue] = React.useState<
    SelectValue<OPT, IsMultiple> | undefined
  >(value)
  const [innerOption, setInnerOption] = React.useState<
    SelectOPT<OPT, IsMultiple> | undefined
  >(
    (Array.isArray(value)
      ? options.filter((opt) => value.includes(opt.value))
      : options.find((opt) => opt.value === value)) as SelectOPT<
      OPT,
      IsMultiple
    >,
  )

  useEffect(() => {
    setInnerValue(value)
    setInnerOption(
      (Array.isArray(value)
        ? options.filter((opt) => value.includes(opt.value))
        : options.find((opt) => opt.value === value)) as SelectOPT<
        OPT,
        IsMultiple
      >,
    )
  }, [value, options])

  const selectedLabelNode = React.useMemo(() => {
    if (innerOption) {
      if (Array.isArray(innerOption)) {
        if (innerOption.length > 0) {
          return innerOption.map((opt) => opt.label).join(',')
        }
      } else {
        return innerOption.label
      }
    }
    return placeholder ? (
      <span className="text-sm">
        {placeholder === defaultPlaceholder ? t('select_item') : placeholder}
      </span>
    ) : null
  }, [innerOption, placeholder])

  const handleSelect = (opt: OPT) => {
    if (multiple === true) {
      const newValue = Array.isArray(innerValue)
        ? innerValue.includes(opt.value)
          ? innerValue.filter((v) => v !== opt.value)
          : [...innerValue, opt.value]
        : [opt.value]
      const newOpt = Array.isArray(innerOption)
        ? innerOption.some((o) => o.value === opt.value)
          ? innerOption.filter((o) => o.value !== opt.value)
          : [...innerOption, opt]
        : [opt]
      setInnerValue(newValue as SelectValue<OPT, IsMultiple>)
      setInnerOption(newOpt as SelectOPT<OPT, IsMultiple>)
      onChange?.(newValue as SelectValue<OPT, IsMultiple>, opt)
    } else {
      if (opt.value === innerValue) {
        return
      }
      setInnerValue(opt.value as SelectValue<OPT, IsMultiple>)
      setInnerOption(opt as SelectOPT<OPT, IsMultiple>)
      onChange?.(opt.value as SelectValue<OPT, IsMultiple>, opt)
      setOpen(false)
    }
    setSearchValue('')
  }

  const handleCreateOption = async () => {
    if (
      !allowCreate ||
      !onCreateOption ||
      !searchValue.trim() ||
      createStatusUtils.isCreating(createStatus)
    ) {
      return
    }

    setCreateStatus(CREATE_STATUS.CREATING)
    try {
      const newOption = await onCreateOption(searchValue.trim())
      if (newOption) {
        handleSelect(newOption)
        setCreateStatus(CREATE_STATUS.FINISH)
        // Reset status after a brief delay to show success feedback
        setTimeout(() => setCreateStatus(CREATE_STATUS.IDLE), 1000)
      }
    } catch (error) {
      console.error('Failed to create option:', error)
      setCreateStatus(CREATE_STATUS.ERROR)
      // Reset status after showing error
      setTimeout(() => setCreateStatus(CREATE_STATUS.IDLE), 2000)
    }
  }

  // 过滤选项 - 用于搜索
  const filteredOptions = React.useMemo(() => {
    if (!searchValue || !showSearch) {
      return options
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase()),
    )
  }, [options, searchValue, showSearch])

  // 判断是否应该显示"创建新选项"
  const shouldShowCreateOption = React.useMemo(() => {
    if (
      !allowCreate ||
      !searchValue.trim() ||
      createStatus === CREATE_STATUS.FINISH
    ) {
      return false
    }

    // 检查是否已经存在相同的选项
    const exactMatch = options.some(
      (option) => option.label.toLowerCase() === searchValue.toLowerCase(),
    )

    return !exactMatch
  }, [allowCreate, searchValue, options])

  const hasSelected =
    !!innerValue || (Array.isArray(innerValue) && innerValue.length > 0)

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={setOpen}
      modal={modal}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline_dark"
          role="combobox"
          disabled={disabled || createStatusUtils.isCreating(createStatus)}
          aria-expanded={open}
          {...buttonProps}
          className={cn(
            'group justify-between border-ring',
            buttonProps?.className,
            hasSelected ? undefined : 'text-muted-foreground',
          )}
          style={{
            width,
            ...buttonProps?.style,
          }}
          icon={
            <span className="relative">
              <ChevronDown
                className={cn(
                  'size-4 shrink-0 text-gray-500 transition-transform duration-100',
                  open ? 'rotate-180' : undefined,
                )}
              />
              {allowClear && hasSelected && !disabled && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation()
                    setInnerValue(undefined)
                    setInnerOption(undefined)
                    onChange?.(undefined, undefined)
                  }}
                  className="absolute left-0 top-0 hidden size-4 items-center justify-center rounded-full bg-slate-400 hover:bg-slate-500 group-hover:flex"
                >
                  <Cross className={cn('size-3 shrink-0 text-white')} />
                </span>
              )}
            </span>
          }
          iconPosition="end"
        >
          {selectedLabelNode}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        {...contentProps}
        style={{
          minWidth: width,
        }}
        className={cn('p-0 w-auto', contentProps?.className)}
      >
        <Command>
          {showSearch && (
            <CommandInput
              {...searchProps}
              value={searchValue}
              onValueChange={setSearchValue}
              disabled={createStatus === CREATE_STATUS.CREATING}
            />
          )}
          <CommandList>
            <CommandEmpty>{empty || t('select_item')}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value as string}
                  onSelect={() => {
                    if (
                      !disabled &&
                      createStatusUtils.isInteractable(createStatus)
                    ) {
                      handleSelect(opt)
                    }
                  }}
                  className={cn(
                    'cursor-pointer',
                    (disabled ||
                      !createStatusUtils.isInteractable(createStatus)) &&
                      'pointer-events-none opacity-50',
                  )}
                >
                  {opt.label}
                  <Check
                    className={cn(
                      'ml-auto',
                      (
                        Array.isArray(innerValue)
                          ? innerValue.includes(opt.value)
                          : innerValue === opt.value
                      )
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
              {shouldShowCreateOption && (
                <CommandItem
                  value={searchValue}
                  onSelect={handleCreateOption}
                  className={cn(
                    'cursor-pointer border-t border-border text-blue-600 hover:text-blue-700',
                    createStatus === CREATE_STATUS.CREATING &&
                      'pointer-events-none opacity-50',
                    createStatus === CREATE_STATUS.ERROR &&
                      'text-red-600 hover:text-red-700',
                    createStatus === CREATE_STATUS.FINISH &&
                      'text-green-600 hover:text-green-700',
                  )}
                >
                  {createStatus === CREATE_STATUS.CREATING ? (
                    <LoaderCircle className="w-4 h-4 mr-2 animate-spin" />
                  ) : createStatus === CREATE_STATUS.ERROR ? (
                    <Cross className="w-4 h-4 mr-2" />
                  ) : createStatus === CREATE_STATUS.FINISH ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  {createStatus === CREATE_STATUS.ERROR
                    ? t('create_failed')
                    : createStatus === CREATE_STATUS.FINISH
                      ? t('create_success')
                      : createText ||
                        t('create_option', { value: searchValue })}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
